// src/lib/TextExtractor.js

/**
 * TextExtractor - Utility for extracting text before specific markers
 * in Word documents for medical report processing
 */
export class TextExtractor {
  /**
   * Check if cursor is positioned after a marker word with nothing in between
   * @param {string} markerWord - The word to search for (e.g., "Conclusion")
   * @returns {Promise<{isAfterMarker: boolean, textBefore: string, markerPosition: number}>}
   */
  static async getTextBeforeMarker(markerWord) {
    if (typeof Word === "undefined" || typeof Word.run !== "function") {
      console.warn("Word API not available");
      return { isAfterMarker: false, textBefore: "", markerPosition: -1 };
    }

    try {
      return await Word.run(async (context) => {
        const selection = context.document.getSelection();
        const body = context.document.body;

        // Load the full document text and selection position
        body.load("text");
        selection.load("text");
        
        await context.sync();

        const fullText = body.text;
        const selectionText = selection.text;
        
        // Get cursor position in document
        // Create a range from start of document to current selection
        const docStart = body.getRange(Word.RangeLocation.start);
        const selectionStart = selection.getRange(Word.RangeLocation.start);
        
        docStart.load("text");
        selectionStart.load("text");
        
        await context.sync();
        
        // Get text from document start to cursor
        const rangeToSelection = docStart.expandTo(selectionStart);
        rangeToSelection.load("text");

        await context.sync();

        const textBeforeCursor = rangeToSelection.text;

        // Find the last occurrence of the marker word (case-insensitive)
        const markerRegex = new RegExp(`(?<!\\S)${markerWord}(?!\\S)`, "gi");
        const matches = [...textBeforeCursor.matchAll(markerRegex)];

        if (matches.length === 0) {
          return { 
            isAfterMarker: false, 
            textBefore: "", 
            markerPosition: -1 
          };
        }
        
        // Get the last occurrence
        const lastMatch = matches[matches.length - 1];
        const markerPosition = lastMatch.index;
        const markerEndPosition = markerPosition + lastMatch[0].length;
        
        // Check if there's only whitespace between marker and cursor
        const textBetween = textBeforeCursor.substring(markerEndPosition).trim();
        
        if (textBetween.length > 0) {
          // There's content between marker and cursor
          return { 
            isAfterMarker: false, 
            textBefore: "", 
            markerPosition: markerPosition 
          };
        }
        
        // Extract text before the marker
        const textBeforeMarker = textBeforeCursor.substring(0, markerPosition).trim();
        
        return {
          isAfterMarker: true,
          textBefore: textBeforeMarker,
          markerPosition: markerPosition,
          fullTextBeforeCursor: textBeforeCursor
        };
      });
    } catch (err) {
      console.error("Error extracting text before marker:", err);
      return { isAfterMarker: false, textBefore: "", markerPosition: -1 };
    }
  }

  /**
   * Monitor cursor position and trigger callback when positioned after marker
   * @param {string} markerWord - The marker word to watch for
   * @param {Function} callback - Function to call with extracted text
   * @param {number} debounceMs - Debounce delay in milliseconds
   */
  static setupCursorMonitoring(markerWord, callback, debounceMs = 300) {
    let debounceTimer = null;
    
    const checkPosition = async () => {
      const result = await TextExtractor.getTextBeforeMarker(markerWord);
      
      if (result.isAfterMarker) {
        callback(result);
      }
    };

    const debouncedCheck = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(checkPosition, debounceMs);
    };

    // Return cleanup function
    return {
      check: debouncedCheck,
      cleanup: () => clearTimeout(debounceTimer)
    };
  }

  /**
   * Extract text between two markers
   * @param {string} startMarker - Start boundary marker
   * @param {string} endMarker - End boundary marker
   * @returns {Promise<{text: string, found: boolean}>}
   */
  static async getTextBetweenMarkers(startMarker, endMarker) {
    if (typeof Word === "undefined" || typeof Word.run !== "function") {
      console.warn("Word API not available");
      return { text: "", found: false };
    }

    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        body.load("text");
        
        await context.sync();
        
        const fullText = body.text;
        
        const startRegex = new RegExp(`\\b${startMarker}\\b`, "i");
        const endRegex = new RegExp(`\\b${endMarker}\\b`, "i");
        
        const startMatch = fullText.match(startRegex);
        const endMatch = fullText.match(endRegex);
        
        if (!startMatch || !endMatch) {
          return { text: "", found: false };
        }
        
        const startPos = startMatch.index + startMatch[0].length;
        const endPos = endMatch.index;
        
        if (startPos >= endPos) {
          return { text: "", found: false };
        }
        
        const extractedText = fullText.substring(startPos, endPos).trim();
        
        return { text: extractedText, found: true };
      });
    } catch (err) {
      console.error("Error extracting text between markers:", err);
      return { text: "", found: false };
    }
  }
}